/**
 * CustomersListPage (B1.12 — Claude scaffolding).
 *
 * Status: interim stub. Renders a tablet-first table of customer summaries
 * using the mock CRM API. When the Lovable-designed detail view (B1.12)
 * lands, this page stays; only the detail-view gets swapped for the
 * Lovable primitives.
 *
 * @see PETOTA_BUSINESS_UX_BLUEPRINT §B1.12
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CaretRight,
  MagnifyingGlass,
  PawPrint,
  Plus,
  Sparkle,
  Star,
  UsersThree,
} from '@phosphor-icons/react';
import { QuickCustomerModal } from '@/components/customers/QuickCustomerModal';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { Input } from '@/shared/ui/Input';
import { Skeleton } from '@/shared/ui/Skeleton';
import { StatCard } from '@/shared/ui/StatCard';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { cn } from '@/shared/utils/cn';
import { formatDate, formatMoney } from '@/shared/utils/formatters';
import { useCustomerSummaries, type CustomerSummary } from '@/features/customers';
import type { LoyaltyTier } from '@/features/appointments/types';

type FilterMode = 'all' | 'vip' | 'upcoming';

const TIER_TONE: Record<LoyaltyTier, 'neutral' | 'info' | 'warning' | 'success'> = {
  bronze: 'neutral',
  silver: 'info',
  gold: 'warning',
  platinum: 'success',
};

export function CustomersListPage(): JSX.Element {
  const { t } = useTranslation('customers');
  const { t: tCommon } = useTranslation('common');
  const { data: summaries, isLoading } = useCustomerSummaries();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterMode>('all');
  const [quickAddOpen, setQuickAddOpen] = useState(false);

  const filtered = useMemo<CustomerSummary[]>(() => {
    const list = summaries ?? [];
    const q = search.trim().toLocaleLowerCase('tr-TR');
    return list.filter(c => {
      if (filter === 'vip' && !c.isVip) return false;
      if (filter === 'upcoming' && c.upcomingAppointmentCount === 0) return false;
      if (!q) return true;
      const haystack = [c.fullName, c.phone, c.email ?? ''].join(' ').toLocaleLowerCase('tr-TR');
      return haystack.includes(q);
    });
  }, [summaries, search, filter]);

  const stats = useMemo(() => {
    const list = summaries ?? [];
    const total = list.length;
    const vip = list.filter(c => c.isVip).length;
    const upcoming = list.reduce((sum, c) => sum + c.upcomingAppointmentCount, 0);
    const spend = list.reduce((sum, c) => sum + (c.totalSpend ?? 0), 0);
    return { total, vip, upcoming, spend };
  }, [summaries]);

  return (
    <div className="px-biz-card-lg py-biz-content-lg max-w-[1400px] mx-auto flex flex-col gap-biz-content-lg">
      <header className="flex flex-col gap-biz-content">
        <div className="flex items-center justify-between gap-biz-content-lg flex-wrap">
          <div>
            <h1 className="text-biz-2xl font-bold biz-heading">{t('page.title')}</h1>
            <p className="text-biz-sm text-biz-muted-foreground mt-1">
              {t('page.subtitle')}
            </p>
          </div>
          <Button
            type="button"
            leftIcon={<Plus size={18} />}
            onClick={() => setQuickAddOpen(true)}
          >
            Hızlı müşteri
          </Button>
        </div>
      </header>

      <section
        aria-label={t('stats.total')}
        className="grid grid-cols-2 md:grid-cols-4 gap-biz-content-lg"
      >
        <StatCard
          label={t('stats.total')}
          value={stats.total}
          icon={<UsersThree weight="regular" size={18} aria-hidden="true" />}
        />
        <StatCard
          label={t('stats.vip')}
          value={stats.vip}
          tone="warning"
          icon={<Star weight="fill" size={18} aria-hidden="true" />}
        />
        <StatCard
          label={t('stats.upcoming')}
          value={stats.upcoming}
          tone="info"
        />
        <StatCard
          label={t('stats.spendThisMonth')}
          value={formatMoney(stats.spend, 'TRY')}
          tone="success"
          icon={<Sparkle weight="regular" size={18} aria-hidden="true" />}
        />
      </section>

      <Card className="overflow-hidden">
        <div className="px-biz-card py-biz-content-lg flex items-center gap-biz-content-lg flex-wrap border-b border-biz-border">
          <div className="relative flex-1 min-w-[240px]">
            <MagnifyingGlass
              weight="regular"
              size={18}
              aria-hidden="true"
              className="absolute left-biz-content-lg top-1/2 -translate-y-1/2 text-biz-muted-foreground pointer-events-none"
            />
            <Input
              aria-label={t('search.placeholder')}
              placeholder={t('search.placeholder')}
              className="pl-10"
              value={search}
              onChange={event => setSearch(event.target.value)}
            />
          </div>
          <div className="flex items-center gap-1 rounded-biz-btn border border-biz-border p-1 bg-biz-muted">
            {(['all', 'vip', 'upcoming'] as FilterMode[]).map(mode => (
              <button
                key={mode}
                type="button"
                onClick={() => setFilter(mode)}
                className={cn(
                  'min-h-biz-touch px-biz-content-lg rounded-biz-btn text-biz-sm font-medium',
                  'transition-colors duration-biz-fast',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring',
                  filter === mode
                    ? 'bg-biz-surface-elevated text-biz-foreground shadow-biz-sm'
                    : 'text-biz-muted-foreground hover:text-biz-foreground',
                )}
              >
                {t(`filters.${mode === 'all' ? 'all' : mode === 'vip' ? 'vipOnly' : 'activeOnly'}`)}
              </button>
            ))}
          </div>
        </div>

        <div className="relative overflow-x-auto">
          <table className="w-full text-biz-sm">
            <thead className="bg-biz-muted/40">
              <tr className="text-left text-biz-xs uppercase tracking-wider text-biz-muted-foreground">
                <th className="px-biz-card-lg py-biz-content font-medium">{t('list.columns.customer')}</th>
                <th className="px-biz-content-lg py-biz-content font-medium">{t('list.columns.contact')}</th>
                <th className="px-biz-content-lg py-biz-content font-medium">{t('list.columns.pets')}</th>
                <th className="px-biz-content-lg py-biz-content font-medium">{t('list.columns.loyalty')}</th>
                <th className="px-biz-content-lg py-biz-content font-medium">{t('list.columns.lastVisit')}</th>
                <th className="px-biz-content-lg py-biz-content font-medium">{t('list.columns.nextVisit')}</th>
                <th className="px-biz-card-lg py-biz-content font-medium text-right">
                  <span className="sr-only">{t('list.columns.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, idx) => (
                  <tr key={`skeleton-${idx}`} className="border-t border-biz-border">
                    <td className="px-biz-card-lg py-biz-content-lg">
                      <Skeleton className="h-5 w-40" />
                    </td>
                    <td className="px-biz-content-lg py-biz-content-lg" colSpan={5}>
                      <Skeleton className="h-4 w-full" />
                    </td>
                    <td className="px-biz-card-lg py-biz-content-lg" />
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-biz-card-lg py-biz-content-xl text-center text-biz-muted-foreground">
                    {search.trim() ? t('search.noResults') : t('list.empty')}
                  </td>
                </tr>
              ) : (
                filtered.map(c => (
                  <CustomerRow key={c.id} customer={c} />
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
      <QuickCustomerModal open={quickAddOpen} onClose={() => setQuickAddOpen(false)} />
    </div>
  );

  function CustomerRow({ customer }: { customer: CustomerSummary }): JSX.Element {
    const tier = customer.loyaltyTier;
    return (
      <tr className="border-t border-biz-border hover:bg-biz-muted/30 transition-colors duration-biz-fast">
        <td className="px-biz-card-lg py-biz-content-lg">
          <Link
            to={`/customers/${customer.id}`}
            className="flex items-center gap-biz-content-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring rounded-biz-btn"
          >
            <Avatar customer={customer} />
            <span className="flex flex-col">
              <span className="font-medium text-biz-foreground flex items-center gap-2">
                {customer.fullName}
                {customer.isVip ? (
                  <span className="inline-flex items-center gap-1 text-biz-warning text-biz-xs font-semibold">
                    <Star weight="fill" size={12} aria-hidden="true" />
                    {t('list.vipBadge')}
                  </span>
                ) : null}
              </span>
              <span className="text-biz-xs text-biz-muted-foreground">
                {customer.email ?? tCommon('labels.na', { defaultValue: '—' })}
              </span>
            </span>
          </Link>
        </td>
        <td className="px-biz-content-lg py-biz-content-lg biz-numeric text-biz-muted-foreground">
          {customer.phone}
        </td>
        <td className="px-biz-content-lg py-biz-content-lg">
          <span className="inline-flex items-center gap-1 text-biz-muted-foreground">
            <PawPrint weight="regular" size={16} aria-hidden="true" />
            {t('list.petCount', { count: customer.petCount })}
          </span>
        </td>
        <td className="px-biz-content-lg py-biz-content-lg">
          {tier ? (
            <StatusBadge
              variant={TIER_TONE[tier]}
              label={t(`loyalty.tier.${tier}`)}
            />
          ) : (
            <span className="text-biz-muted-foreground">{t('list.never')}</span>
          )}
        </td>
        <td className="px-biz-content-lg py-biz-content-lg text-biz-muted-foreground">
          {customer.lastVisitAt ? formatDate(customer.lastVisitAt) : t('list.never')}
        </td>
        <td className="px-biz-content-lg py-biz-content-lg text-biz-muted-foreground">
          {customer.nextAppointmentAt ? formatDate(customer.nextAppointmentAt) : t('list.never')}
        </td>
        <td className="px-biz-card-lg py-biz-content-lg text-right">
          <Link
            to={`/customers/${customer.id}`}
            aria-label={`${customer.fullName} — ${t('page.subtitle')}`}
            className="inline-flex items-center justify-center min-h-biz-touch min-w-biz-touch rounded-biz-btn text-biz-muted-foreground hover:bg-biz-muted hover:text-biz-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring"
          >
            <CaretRight weight="regular" size={18} aria-hidden="true" />
          </Link>
        </td>
      </tr>
    );
  }
}

function Avatar({ customer }: { customer: CustomerSummary }): JSX.Element {
  const initials = customer.fullName
    .split(' ')
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('');
  return customer.avatarUrl ? (
    <img
      src={customer.avatarUrl}
      alt=""
      aria-hidden="true"
      className="w-10 h-10 rounded-biz-full object-cover border border-biz-border"
      loading="lazy"
    />
  ) : (
    <div
      aria-hidden="true"
      className="w-10 h-10 rounded-biz-full bg-biz-primary-subtle text-biz-primary flex items-center justify-center text-biz-sm font-semibold"
    >
      {initials || '?'}
    </div>
  );
}
